import { sizeProps } from "#ce/composables/useSize";
import { booleanAttribute } from "#ce/utils/props";

export const variantOptions = ["tonal", "flat", "outlined", "plain"];

// The four steps of the shared size scale the chip styles. A wider value would pass the shared
// validator and then render at the default, so the chip narrows it to what it draws.
export const sizeOptions = ["x-small", "small", "medium", "large"];

export const colorOptions = ["primary", "secondary", "tertiary", "danger", "error", "warning", "success", "info"];

// We had bugs in the past, where the props were mutated. Apply Object.freeze() to prevent this.
export const props = Object.freeze({
  /**
   * Scale of the chip, tightening padding and font size. `medium` is the default rendering.
   *
   * @type {String}
   * @default undefined
   * @values x-small, small, medium, large
   * @storybook-control select
   */
  size: {
    ...sizeProps.size,
    validator: (value) => sizeOptions.includes(value),
  },
  /**
   * Scale applied on small screens, falling back to `size` when unset.
   *
   * @type {String}
   * @default undefined
   * @values x-small, small, medium, large
   * @storybook-control select
   * @storybook-table-subcategory Small Screen
   */
  sizeSm: {
    ...sizeProps.sizeSm,
    validator: (value) => sizeOptions.includes(value),
  },
  /**
   * Text rendered inside the chip. The default slot takes precedence, for a chip that needs markup.
   *
   * @type {String}
   * @default undefined
   */
  label: {
    type: String,
    default: undefined,
  },
  /**
   * Scale applied on medium screens, falling back to `size` when unset.
   *
   * @type {String}
   * @default undefined
   * @values x-small, small, medium, large
   * @storybook-control select
   * @storybook-table-subcategory Medium Screen
   */
  sizeMd: {
    ...sizeProps.sizeMd,
    validator: (value) => sizeOptions.includes(value),
  },
  /**
   * Visual treatment of the chip surface.
   *
   * @type {String}
   * @default "tonal"
   * @values tonal, flat, outlined, plain
   */
  variant: {
    type: String,
    validator: (value) => variantOptions.includes(value),
    default: "tonal",
  },
  /**
   * Variant applied on small screens, falling back to `variant` when unset.
   *
   * @type {String}
   * @default undefined
   * @values tonal, flat, outlined, plain
   * @storybook-table-subcategory Small Screen
   */
  smVariant: {
    type: String,
    validator: (value) => variantOptions.includes(value),
    default: undefined,
  },
  /**
   * Colour role the chip is drawn from. Each role pairs its surface with the platform's own text
   * colour, so a dark fill takes a light font. The variant decides whether the role lands on the
   * surface, the outline, or the text.
   *
   * @type {String}
   * @default undefined
   * @values primary, secondary, tertiary, danger, error, warning, success, info
   * @storybook-control select
   */
  color: {
    type: String,
    validator: (value) => colorOptions.includes(value),
    default: undefined,
  },
  /**
   * Fully rounded ends, rather than the default corner radius.
   *
   * @type {Boolean|String}
   * @default false
   */
  pill: booleanAttribute(),
  /**
   * Render a remove button that emits `close`. The chip does not remove itself — the consumer owns
   * the list, so it decides what a close means.
   *
   * @type {Boolean|String}
   * @default false
   */
  closable: booleanAttribute(),
  /**
   * Icon name rendered before the text. The `prepend` slot takes precedence.
   *
   * @type {String}
   * @default undefined
   */
  prependIcon: {
    type: String,
    default: undefined,
  },
  /**
   * Sets a `data-testid` on the remove button, the element a test clicks.
   *
   * @type {String}
   * @default undefined
   */
  closeTestId: {
    type: String,
    default: undefined,
  },
});
